import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useEffect, useState } from 'react';
import AddressEditDialog from './address-edit-dialog';
import { UserFormData } from '../../add-user-dialog/user-form-page';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddCircle from '@mui/icons-material/AddCircle';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import ExpandMore from '@mui/icons-material/ExpandMore';

interface AddressAccordionProps {
  control: Control<UserFormData>;
  errors: FieldErrors<UserFormData>;
  setValue: UseFormSetValue<UserFormData>;
  watch: UseFormWatch<UserFormData>;
  getValues: UseFormGetValues<UserFormData>;
  disabled: boolean;
}

export default function AddressAccordion({
  control,
  errors,
  disabled,
  watch,
  setValue,
  getValues,
}: AddressAccordionProps) {
  const [open, setOpen] = useState(false);
  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [clickedIndex, setClickedIndex] = useState(0);

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'addresses',
  });

  const menuItems = [
    { value: 'custom', label: 'Custom' },
    { value: 'home', label: 'Home' },
    { value: 'other', label: 'Other' },
    { value: 'work', label: 'Work' },
  ];

  const addressErrors = errors['addresses'];
  // close accordion if there are no items in the accordion
  useEffect(() => {
    if (fields.length === 0) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [fields]);

  // open accordion if there is an error in the accordion
  useEffect(() => {
    if (addressErrors) {
      setOpen(true);
    }
  }, [addressErrors]);

  return (
    <>
      <Accordion
        expanded={open}
        onChange={() => {
          if (fields.length === 0) return;
          setOpen(!open);
        }}
      >
        <AccordionSummary
          expandIcon={
            <IconButton
              component="span"
              disabled={fields.length === 0}
              sx={{ mr: -1 }}
              disableRipple
            >
              <ExpandMore />
            </IconButton>
          }
        >
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <Typography
              sx={{
                color: (theme) =>
                  addressErrors ? theme.palette.error.main : 'inherit',
              }}
            >
              Addresses
            </Typography>
            <Tooltip title="Add" arrow>
              <span>
                <IconButton
                  component="span"
                  size="small"
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    append({
                      type: '',
                      customType: '',
                      sourceIsStructured: false,
                      formatted: '',
                      poBox: '',
                      extendedAddress: '',
                      streetAddress: '',
                      locality: '',
                      region: '',
                      postalCode: '',
                      country: '',
                      primary: false,
                    });
                  }}
                >
                  <AddCircle />
                </IconButton>
              </span>
            </Tooltip>
            {fields.length > 0 ? (
              <Chip label={fields.length} color="primary" size="small" />
            ) : null}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {fields.map((item, index) => {
            const formattedAddress = watch(`addresses.${index}.formatted`);
            const typeField = watch(`addresses.${index}.type`);

            return (
              <Box key={item.id}>
                <Stack
                  gap={1}
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ my: 1.5 }}
                >
                  <Stack flexDirection="row" alignItems="center" gap={2}>
                    <Controller
                      rules={{ required: 'This field is required.' }}
                      name={`addresses.${index}.type`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required
                          inputRef={field.ref}
                          inputProps={{
                            id: `addresses-type-${index}`,
                          }}
                          InputLabelProps={{
                            htmlFor: `addresses-type-${index}`,
                          }}
                          name="addresses"
                          select
                          disabled={disabled}
                          label={'Type'}
                          error={!!addressErrors?.[index]?.type}
                          helperText={
                            addressErrors?.[index]?.type
                              ? 'This field is required.'
                              : ''
                          }
                          sx={{
                            minWidth: 150,
                          }}
                        >
                          {menuItems.map((item) => (
                            <MenuItem value={item.value} key={item.label}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                    {typeField === 'custom' && (
                      <Controller
                        rules={{ required: 'This field is required.' }}
                        name={`addresses.${index}.customType`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required
                            inputRef={field.ref}
                            inputProps={{
                              id: `addresses-customType-${index}`,
                            }}
                            InputLabelProps={{
                              htmlFor: `addresses-customType-${index}`,
                            }}
                            disabled={disabled}
                            name="addresses"
                            label={`Custom Type`}
                            error={!!addressErrors?.[index]?.customType}
                            helperText={
                              addressErrors?.[index]?.customType
                                ? 'This field is required.'
                                : ''
                            }
                            sx={{
                              minWidth: 160,
                            }}
                          />
                        )}
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {`${formattedAddress}`}
                    </Typography>
                    <Tooltip title={'Edit Address'} arrow>
                      <IconButton
                        component="span"
                        size="small"
                        disabled={disabled}
                        onClick={() => {
                          setClickedIndex(index);
                          setOpenAddressDialog(true);
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  {/* actions */}
                  <Stack flexDirection="row" gap={2}>
                    <Tooltip title="Move Down" arrow>
                      <span>
                        <IconButton
                          component="span"
                          disabled={index === fields.length - 1 || disabled}
                          size="small"
                          onClick={() => {
                            move(index, index + 1);
                          }}
                        >
                          <ArrowDownward />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move Up" arrow>
                      <span>
                        <IconButton
                          component="span"
                          disabled={index === 0 || disabled}
                          size="small"
                          onClick={() => {
                            move(index, index - 1);
                          }}
                        >
                          <ArrowUpward />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <span>
                        <IconButton
                          component="span"
                          size="small"
                          disabled={disabled}
                          onClick={() => {
                            remove(index);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
                {index !== fields.length - 1 && <Divider />}
              </Box>
            );
          })}
        </AccordionDetails>
      </Accordion>
      {openAddressDialog ? (
        <AddressEditDialog
          openAddressDialog={openAddressDialog}
          setOpenAddressDialog={setOpenAddressDialog}
          clickedIndex={clickedIndex}
          setValue={setValue}
          disabled={!!disabled}
          getValues={getValues}
        />
      ) : null}
    </>
  );
}
